/* eslint-disable no-console */
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { CONTRACTING_SOURCE_APPLICATION } from '@makemydeal/dr-dash-bff-types';
import { actions as offerReduxActions } from '@makemydeal/dr-offer-redux';
import {
    type DashMiddleware,
    type DashNext,
    type DashStore,
    type ReduxStore,
    dealerSelectors,
    featureToggleSelectors,
    initActionTypes
} from '@makemydeal/dr-shared-store';
import type { StateTree } from '@makemydeal/dr-dash-types';
import {
    CONTRACTING_CANCEL_FAILURE_EVENT,
    CONTRACTING_CLOSE_MODAL,
    CONTRACTING_EVENT_RECEIVED,
    CONTRACTING_FS_RESPONSE_RECEIVED,
    CONTRACTING_IN_PROGRESS,
    CONTRACTING_OFFER_SAVE_FAILURE_EVENT,
    CONTRACTING_OFFER_SAVE_SUCCESS_EVENT,
    CONTRACTING_SAVE_FAILED_WITH_SPOT_ERRORS,
    CONTRACTING_SAVED_WITH_ERRORS_EVENT,
    CONTRACTING_SET_FORM_CONTRACT_TYPE,
    CONTRACTING_STAGE_CANCEL_FAILURE,
    CONTRACTING_STAGE_CANCEL_REQUEST_FAILURE_EVENT,
    CONTRACTING_STAGE_CANCEL_SUCCESS,
    CONTRACTING_STAGE_COPY_AWAITING_CANCEL,
    CONTRACTING_STAGE_COPY_FAILURE,
    CONTRACTING_STAGE_COPY_REQUEST,
    CONTRACTING_STAGE_COPY_REQUEST_FAILURE_EVENT,
    CONTRACTING_STAGE_COPY_SUCCESS,
    CONTRACTING_STAGE_START_FAILURE,
    CONTRACTING_STAGE_START_REQUEST_FAILURE_EVENT,
    CONTRACTING_STAGE_VERIFY_FAILURE,
    CONTRACTING_STAGE_VERIFY_REQUEST_FAILURE_EVENT,
    CONTRACTING_START_FAILURE_EVENT,
    CONTRACTING_STATUS_SUCCESS,
    CONTRACTING_TIMEOUT_FAILURE,
    CONTRACTING_VERIFY_FAILURE_EVENT,
    CONTRACTING_VERIFY_SUCCESS_EVENT,
    CONTRACTING_WAITING_FOR_FS,
    COPY_CREDIT_APP_DEAL_UPDATE_FAILURE,
    COPY_CREDIT_APP_DEAL_UPDATE_SUCCESS,
    COPY_CREDIT_APP_EVENT_RECEIVED,
    COPY_CREDIT_APP_HTTP_FAILURE
} from '../actionTypes/contractingActionTypes';
import { OFFER_SAVE_FAILURE, OFFER_SAVE_SUCCESS } from '../actionTypes/offerActionTypes';
import { AIO_DXG_EVENTS } from '../actionTypes/socketActionTypes';
import {
    contractingCloseModal,
    contractingSendCancel,
    contractingSendCopyCreditApp,
    contractingSendStart,
    contractingSendVerify,
    contractingStartProgress,
    getContractingPartyId,
    getContractingSourcePartnerId,
    getContractingStatus,
    revalidateContractAfterApiFailure,
    updateDealForCopyCreditApp
} from '../actions/contractingActionCreators';
import { getCreditAppReferenceId } from '../selectors/creditAppSelectors';
import { setRefreshContractValidationState } from '../actions/vehicleProtectionActionCreators';
import { getContractingTimeout } from '../selectors/config';
import {
    shouldCancelForDifferentLender,
    validateEvent,
    shouldCancelVerification,
    checkIfSpotError
} from '../utils/contractingUtils';

import * as navigationActionCreators from '../actions/navigationActionCreators';
import * as offerActionCreators from '../actions/offerActionCreators';

import { getDealXgVersion } from '../selectors/offerInfoSelectors';
import { getCurrentOfferType, getDealXgId } from '../selectors/offerRedux';
import { getDealRefId } from '../selectors/offerSelectors';
import { currentOfferHasUnsavedChanges } from '../selectors/unsavedChangesSelectors';

import { ContractStatusBadgeInfo, ContractStatusCodes, RefreshContractValidationState } from '@makemydeal/dr-dash-types';
import { CASH } from '@makemydeal/dr-platform-types';
import type { ContractStatuses } from '@makemydeal/dr-shared-types';
import {
    computeStageDurationMs,
    getContractingCorrelationId,
    getContractingDurations,
    getContractingStage,
    getCopyAttempts,
    getCopyInFlightSince,
    getCopyStage,
    getFailureEvent,
    getIsContractingContext,
    getSaveOfferRequestEndTimeMs,
    isContractCancelled,
    isInProgress
} from '../selectors/contractingSelectors';
import { shouldCancelContracts } from '../utils/contractingUtils';
/** one minute */
const CONTRACTING_DEFAULT_TIMEOUT_MS = 60e3;
const CONTRACTING_CANCEL_TIMEOUT_MS = 15e3;
const FS_SUCCESS_TIMEOUT_MS = 10e3;
/** Stall budget between the BFF 201 ack and the terminal ws frame. */
const COPY_STALL_TIMEOUT_MS = 15e3;
/** Pinned target platform for the copy-credit-app flow; mirrors `contractingActionCreators`. */
const COPY_TARGET_PLATFORM_ID = 'DTC';

let timeoutTimer: any = null;
let cancelTimer: any = null;
let fsResponseTimer: any = null;
let copyStallTimer: any = null;

let pendingCopyCreditAppMessage: Record<string, unknown> | null = null;

const bridgeDealUpdateToCopySuccess = (store: ReduxStore<StateTree>): void => {
    if (pendingCopyCreditAppMessage) {
        store.dispatch({ type: CONTRACTING_STAGE_COPY_SUCCESS, payload: pendingCopyCreditAppMessage });
        pendingCopyCreditAppMessage = null;
    }
    store.dispatch(contractingSendStart());
};

export const middleware: DashMiddleware = (store: DashStore) => (next: DashNext) => (action: AnyFSA) => {
    const prevState = store.getState();

    next(action);

    const state = store.getState();

    switch (action.type) {
        case initActionTypes.INIT_SUCCESS: {
            const isDisplayContractStatusEnabled = featureToggleSelectors.isDisplayContractStatusEnabled(state);
            if (isDisplayContractStatusEnabled && getDealRefId(state)) {
                store.dispatch(getContractingStatus());
            }
            break;
        }

        case CONTRACTING_STATUS_SUCCESS: {
            const contractStatusCode = action.payload?.contractStatusCode;
            if (!contractStatusCode || !(contractStatusCode in ContractStatusBadgeInfo)) {
                logNewRelicPageAction('MV:Contracting - Unknown Status', {
                    dealRefId: getDealRefId(state),
                    contractStatus: action.payload?.contractStatus ?? null,
                    contractStatusCode: contractStatusCode ?? null
                });
            }
            break;
        }

        case CONTRACTING_SET_FORM_CONTRACT_TYPE: {
            logNewRelicPageAction('MV:Contracting - Paper Form Type Selected', {
                contractType: 'Paper',
                formContractType: action.payload?.formContractType,
                dealerStateCode: dealerSelectors.getDealerStateCode(state)
            });
            break;
        }

        case CONTRACTING_SAVED_WITH_ERRORS_EVENT: {
            store.dispatch(revalidateContractAfterApiFailure());
            break;
        }

        /**
         * Cash deals may not receive a cancel event, so wait 15 seconds before proceeding with the start call
         */
        case CONTRACTING_STAGE_CANCEL_SUCCESS: {
            const currentOfferType = getCurrentOfferType(state);
            if (currentOfferType === CASH) {
                cancelTimer = setTimeout(() => {
                    const delayedState = store.getState();
                    if (getContractingStage(delayedState) === 'cancel') {
                        store.dispatch(contractingSendStart());
                    }
                }, CONTRACTING_CANCEL_TIMEOUT_MS);
            }
            break;
        }
        case CONTRACTING_CLOSE_MODAL: {
            pendingCopyCreditAppMessage = null;
            clearTimeout(timeoutTimer);
            clearTimeout(fsResponseTimer);
            // Clear the copy stall timer so a late expiry can't dispatch a
            // spurious CONTRACTING_STAGE_COPY_FAILURE after modal dismissal.
            clearTimeout(copyStallTimer);
            clearTimeout(cancelTimer);
            const inProgress = isInProgress(prevState);
            const dealRefId = getDealRefId(state);
            if (inProgress) {
                const currentOfferType = getCurrentOfferType(state);
                const stage = getContractingStage(prevState);
                const currentCorrelationId = getContractingCorrelationId(prevState);

                const failureEvent = getFailureEvent(prevState);
                const attributes = {
                    ...getContractingDurations(prevState),
                    stage,
                    success: action.meta?.success ?? null,
                    currentOfferType,
                    failureEvent,
                    correlationId: currentCorrelationId,
                    dealRefId
                };
                const message = 'MV:Contracting - Workflow complete';

                console.debug(message, attributes);
                logNewRelicPageAction(message, attributes);
            } else {
                logNewRelicPageAction('MV:Contracting - modal dismissed', { inProgress: false, dealRefId });
            }
            break;
        }
        case OFFER_SAVE_SUCCESS: {
            const currentOfferType = getCurrentOfferType(state);
            const dealRefId = getDealRefId(state);

            // Saving unsaved changes was successful
            if (action.meta?.source === CONTRACTING_IN_PROGRESS) {
                const message = 'MV:Contracting - DealAPI update complete';
                const attributes = {
                    currentOfferType,
                    stage: 'DealAPI',
                    success: true,
                    dealRefId
                };
                logNewRelicPageAction(message, attributes);
                console.debug('Contracting: DXG Save Success', attributes);

                store.dispatch({
                    type: CONTRACTING_OFFER_SAVE_SUCCESS_EVENT,
                    payload: { timestampMs: Date.now() }
                });
                // Enter waiting state for FS success event
                store.dispatch({ type: CONTRACTING_WAITING_FOR_FS });

                // Set timeout for FS response event
                fsResponseTimer = setTimeout(() => {
                    const currentState = store.getState();
                    const contractingStage = getContractingStage(currentState);

                    if (contractingStage === 'waitingForFs') {
                        logNewRelicPageAction('MV:Contracting - FS Response Timeout', {
                            stage: 'WaitingForFS',
                            dealRefId,
                            currentOfferType,
                            timeoutMs: FS_SUCCESS_TIMEOUT_MS
                        });

                        store.dispatch(contractingStartProgress());
                    }
                }, FS_SUCCESS_TIMEOUT_MS);

                const waitingMessage = 'MV:Contracting - Waiting for FS Response';
                const waitingAttributes = {
                    stage: 'WaitingForFS',
                    currentOfferType,
                    dealRefId
                };
                logNewRelicPageAction(waitingMessage, waitingAttributes);
                console.debug(waitingMessage, waitingAttributes);
            }
            break;
        }

        case OFFER_SAVE_FAILURE: {
            if (action.meta?.source === CONTRACTING_IN_PROGRESS) {
                const currentOfferType = getCurrentOfferType(state);
                const message = 'MV:Contracting - DealAPI update failure';
                const dealXgId = getDealXgId(state);
                const dealXgVersion = getDealXgVersion(state) || '';
                const dealRefId = getDealRefId(state);

                const attributes = {
                    stage: 'DealAPI',
                    success: false,
                    currentOfferType,
                    dealXgId,
                    dealXgVersion,
                    dealRefId
                };
                logNewRelicPageAction(message, attributes);
                console.debug(message, attributes);
                store.dispatch(contractingCloseModal(false));
                store.dispatch({
                    type: CONTRACTING_OFFER_SAVE_FAILURE_EVENT,
                    payload: { timestampMs: Date.now() }
                });
            }
            break;
        }

        case offerReduxActions.UPDATED_OFFER_TYPE: {
            const isContractingContext = getIsContractingContext(state);
            if (isContractingContext) {
                // Mark contract validation as pending during contracting so we wait to refresh
                // it until fetch VPP products returns updated protection products for the new offer type.
                store.dispatch(setRefreshContractValidationState(RefreshContractValidationState.PENDING));
            }
            break;
        }

        case AIO_DXG_EVENTS: {
            const currentOfferType = getCurrentOfferType(state);
            const contractingStage = getContractingStage(state);
            const fsEventType = action.payload?.type;

            if (contractingStage === 'waitingForFs' && (fsEventType === 'fsSuccess' || fsEventType === 'fsFailure')) {
                const dealRefId = getDealRefId(state);

                // Clear the timeout since we received the event
                clearTimeout(fsResponseTimer);

                // Mark FS event received
                store.dispatch({ type: CONTRACTING_FS_RESPONSE_RECEIVED, payload: { timeMs: Date.now() } });

                // Calculate and log delay
                const saveEndTime = getSaveOfferRequestEndTimeMs(state);
                const delay = saveEndTime ? Date.now() - saveEndTime : 0;
                // At this point, action.payload must exist since fsEventType was derived from it
                const fsReason = action.payload!.reason;

                // Log with event type for monitoring
                logNewRelicPageAction('MV:Contracting - Received FS event', {
                    stage: 'FSComplete',
                    currentOfferType,
                    dealRefId,
                    delayMs: delay,
                    fsEventType, // 'fsSuccess' or 'fsFailure'
                    fsReason, // May contain 'CreateBadRequest' or 'Other' for failures
                    success: true
                });

                // Proceed with contracting regardless of success/failure
                store.dispatch(contractingStartProgress());

                console.debug(`Contracting: FS ${fsEventType} Received, proceeding with Start Contract`, {
                    currentOfferType,
                    dealRefId,
                    delayMs: delay,
                    fsEventType,
                    reason: fsReason
                });
            }
            break;
        }

        case CONTRACTING_EVENT_RECEIVED: {
            const enableGenerateContracts = dealerSelectors.getEnableGenerateContracts(state);

            /*
            Don't trigger additional contracting logic if generateContracting is not enabled.
            Allows updating contracting event status without requiring full generateContracting flow to be enabled.
            */
            if (!enableGenerateContracts) {
                break;
            }

            const eventName: string = action.payload?.eventName;
            const currentOfferType = getCurrentOfferType(state);
            const dealXgId = getDealXgId(state);
            const dealXgVersion = getDealXgVersion(state);
            const dealRefId = getDealRefId(state);
            const stage = getContractingStage(state);
            const inProgress = isInProgress(state);
            const eventCorrelationId = action.payload?.eventKeyData?.correlationId;
            const eventSourceApplication = action.payload?.eventKeyData?.sourceApplication;
            const currentCorrelationId = getContractingCorrelationId(state);
            const enableMVContractingSourceAppHeader = featureToggleSelectors.enableMVContractingSourceAppHeader(state);
            const isContractErrorMapping = featureToggleSelectors.isContractErrorMappingEnabled(state);
            const statusCode = ContractStatusCodes[eventName as ContractStatuses];

            // Validation check: correlation ID and source application
            if (!validateEvent(enableMVContractingSourceAppHeader, currentCorrelationId, eventName, action.payload?.eventKeyData)) {
                console.debug('Contracting: Validation mismatch', {
                    eventCorrelationId,
                    currentCorrelationId,
                    eventSourceApplication,
                    expectedSourceApplication: CONTRACTING_SOURCE_APPLICATION,
                    eventName
                });
                logNewRelicPageAction('MV:Contracting - Validation Mismatch', {
                    currentOfferType,
                    dealXgId,
                    dealXgVersion,
                    eventName,
                    eventCorrelationId,
                    currentCorrelationId,
                    eventSourceApplication,
                    expectedSourceApplication: CONTRACTING_SOURCE_APPLICATION,
                    dealRefId
                });
                break;
            }
            const logUnexpectedEvent = () => {
                console.debug('Contracting: Unexpected Event', { eventName, stage, ...action.payload, inProgress });
                logNewRelicPageAction('MV:Contracting - Unexpected Event', {
                    currentOfferType,
                    dealXgId,
                    dealXgVersion,
                    eventName,
                    stage,
                    inProgress,
                    dealRefId
                });
            };
            if (!inProgress) {
                logUnexpectedEvent();
                break;
            }

            if (statusCode) {
                store.dispatch({
                    type: CONTRACTING_STATUS_SUCCESS,
                    payload: {
                        dealRefId,
                        description: ContractStatusBadgeInfo[statusCode].label,
                        contractStatus: eventName,
                        contractStatusCode: statusCode
                    }
                });
            }

            const logEvent = () => {
                logNewRelicPageAction('MV:Contracting - Received Event', {
                    currentOfferType,
                    dealXgId,
                    dealXgVersion,
                    dealRefId,
                    eventName,
                    inProgress
                });
                console.debug('Contracting: Received Event', {
                    currentOfferType,
                    eventName,
                    stage,
                    ...action.payload,
                    inProgress
                });
            };

            switch (eventName) {
                case 'Contracting:Cancelled':
                case 'Contracting:CancelFailed': {
                    if (stage !== 'cancel') {
                        logUnexpectedEvent();
                        break;
                    }

                    logEvent();
                    clearTimeout(cancelTimer);
                    if (
                        eventName === 'Contracting:CancelFailed' &&
                        action.payload.payload?.message?.current_state != null &&
                        getCopyStage(state) !== 'awaiting_cancel'
                    ) {
                        // We failed to cancel the contract
                        store.dispatch(contractingCloseModal(false));
                        store.dispatch({
                            type: CONTRACTING_CANCEL_FAILURE_EVENT,
                            payload: action.payload
                        });
                        break;
                    }
                    if (getCopyStage(state) === 'awaiting_cancel') {
                        const dealerId = dealerSelectors.getDealerId(state);
                        const stageDurationMs = computeStageDurationMs(state);
                        store.dispatch(
                            contractingSendCopyCreditApp({
                                correlationId: currentCorrelationId ?? '',
                                dealRefId,
                                sourceCreditAppId: getCreditAppReferenceId(state),
                                sourcePartnerId: getContractingSourcePartnerId(state),
                                targetPlatformId: COPY_TARGET_PLATFORM_ID,
                                partyId: getContractingPartyId(state),
                                dealerId: String(dealerId ?? ''),
                                dealXgDealId: dealXgId
                            })
                        );
                        logNewRelicPageAction('MV:CopyCreditApp - copy-requested', {
                            correlationId: currentCorrelationId,
                            dealRefId,
                            dealXgId,
                            dealerId,
                            stage: 'requesting',
                            stageDurationMs
                        });
                        break;
                    }
                    store.dispatch(contractingSendStart());
                    break;
                }
                case 'Contracting:SavedSuccessfully': {
                    if (stage !== 'start') {
                        logUnexpectedEvent();
                        break;
                    }
                    logEvent();

                    if (shouldCancelVerification(action.payload)) {
                        store.dispatch(contractingCloseModal(false));
                        store.dispatch({
                            type: CONTRACTING_SAVED_WITH_ERRORS_EVENT,
                            payload: action.payload
                        });
                        break;
                    }

                    store.dispatch(contractingSendVerify());
                    break;
                }
                case 'Contracting:SaveFailed': {
                    if (stage !== 'start') {
                        logUnexpectedEvent();
                        break;
                    }
                    logEvent();

                    // Check if error is due to deal being associated with a different lender
                    const isDifferentLenderError = shouldCancelForDifferentLender(action.payload);

                    if (isDifferentLenderError) {
                        const contractAlreadyCancelled = isContractCancelled(state);
                        if (!contractAlreadyCancelled) {
                            logNewRelicPageAction('MV:Contracting - Different Lender Error, Auto-Canceling', {
                                dealRefId,
                                reason: 'already_associated_with_different_lender'
                            });
                            // Keep modal open and dispatch cancel - will auto-retry after cancel success
                            store.dispatch(contractingSendCancel());
                            break;
                        } else {
                            // Already cancelled once, now show error to user
                            logNewRelicPageAction('MV:Contracting - Different Lender Error After Cancel Attempt', {
                                dealRefId,
                                reason: 'cancel_did_not_resolve_lender_conflict'
                            });
                        }
                    }

                    const contractAlreadyCancelled = isContractCancelled(state);
                    const cancelContracts = shouldCancelContracts(action.payload);
                    if (cancelContracts) {
                        if (!contractAlreadyCancelled) {
                            const dealerId = dealerSelectors.getDealerId(state);
                            logNewRelicPageAction('MV:IST Detected', {
                                correlationId: currentCorrelationId,
                                dealRefId,
                                dealXgId,
                                dealerId,
                                currentOfferType,
                                stage: getCopyStage(state)
                            });
                            if (currentOfferType === CASH && getCopyAttempts(state) === 0) {
                                const stageDurationMs = computeStageDurationMs(state);
                                store.dispatch({ type: CONTRACTING_STAGE_COPY_AWAITING_CANCEL });
                                logNewRelicPageAction('MV:CopyCreditApp - intercepted', {
                                    correlationId: currentCorrelationId,
                                    dealRefId,
                                    dealXgId,
                                    dealerId,
                                    stage: 'awaiting_cancel',
                                    stageDurationMs
                                });
                            }
                            store.dispatch(contractingSendCancel());
                            break;
                        } else {
                            logNewRelicPageAction('MV:Contracting - Save Failed second attempt', {
                                dealRefId
                            });
                        }
                    }

                    const isSpotError = checkIfSpotError(action.payload);
                    const isSpotMsgNormalizationEnabled = featureToggleSelectors.isSpotMsgNormalizationEnabled(state);
                    if (isSpotError && isSpotMsgNormalizationEnabled) {
                        store.dispatch(contractingCloseModal(false));
                        store.dispatch({
                            type: CONTRACTING_SAVE_FAILED_WITH_SPOT_ERRORS,
                            payload: action.payload
                        });
                        break;
                    }

                    store.dispatch(contractingCloseModal(false));
                    store.dispatch({
                        type: CONTRACTING_START_FAILURE_EVENT,
                        payload: action.payload,
                        meta: {
                            isContractErrorMapping: isContractErrorMapping
                        }
                    });
                    break;
                }
                case 'ContractingDecisions:Verified':
                case 'ContractingDecisions:VerifiedWithWarnings': {
                    if (stage !== 'verify') {
                        logUnexpectedEvent();
                        break;
                    }
                    logEvent();
                    store.dispatch(contractingCloseModal(true));
                    store.dispatch({
                        type: CONTRACTING_VERIFY_SUCCESS_EVENT,
                        payload: action.payload
                    });
                    store.dispatch(navigationActionCreators.navigateToDDJ());
                    break;
                }
                case 'ContractingDecisions:VerificationFailed': {
                    if (stage !== 'verify') {
                        logUnexpectedEvent();
                        break;
                    }
                    logEvent();
                    store.dispatch(contractingCloseModal(false));
                    store.dispatch({
                        type: CONTRACTING_VERIFY_FAILURE_EVENT,
                        payload: action.payload
                    });
                    break;
                }
                default: {
                    logUnexpectedEvent();
                    break;
                }
            }
            break;
        }

        case CONTRACTING_IN_PROGRESS: {
            if (getContractingStage(state) === undefined && currentOfferHasUnsavedChanges(state)) {
                store.dispatch(offerActionCreators.saveOffer({ source: CONTRACTING_IN_PROGRESS }));
                break;
            }

            // Start timeout
            clearTimeout(timeoutTimer);
            timeoutTimer = setTimeout(() => {
                store.dispatch({ type: CONTRACTING_TIMEOUT_FAILURE });
                store.dispatch(contractingCloseModal(false));
            }, getContractingTimeout(state) ?? CONTRACTING_DEFAULT_TIMEOUT_MS);

            store.dispatch(contractingSendStart());
            break;
        }

        case CONTRACTING_STAGE_CANCEL_FAILURE:
            store.dispatch(contractingCloseModal(false));
            store.dispatch({
                type: CONTRACTING_STAGE_CANCEL_REQUEST_FAILURE_EVENT,
                payload: action.payload
            });
            break;
        case CONTRACTING_STAGE_START_FAILURE:
            store.dispatch(contractingCloseModal(false));
            store.dispatch({
                type: CONTRACTING_STAGE_START_REQUEST_FAILURE_EVENT,
                payload: action.payload
            });
            break;
        case CONTRACTING_STAGE_VERIFY_FAILURE: {
            store.dispatch(contractingCloseModal(false));
            store.dispatch({
                type: CONTRACTING_STAGE_VERIFY_REQUEST_FAILURE_EVENT,
                payload: action.payload
            });
            break;
        }

        case CONTRACTING_STAGE_COPY_REQUEST: {
            clearTimeout(copyStallTimer);
            const dealRefId = getDealRefId(state);
            const dealXgId = getDealXgId(state);
            const dealerId = dealerSelectors.getDealerId(state);
            const currentCorrelationId = getContractingCorrelationId(state);
            const stallTimerArmedAtMs = Date.now();
            copyStallTimer = setTimeout(() => {
                logNewRelicPageAction('MV:CopyCreditApp - stall-timeout', {
                    correlationId: currentCorrelationId,
                    dealRefId,
                    dealXgId,
                    dealerId,
                    timeoutMs: COPY_STALL_TIMEOUT_MS,
                    failureMode: 'stall_timeout',
                    stageDurationMs: Date.now() - stallTimerArmedAtMs
                });
                store.dispatch(contractingCloseModal(false));
                store.dispatch({ type: CONTRACTING_STAGE_COPY_FAILURE });
                store.dispatch({
                    type: CONTRACTING_STAGE_COPY_REQUEST_FAILURE_EVENT,
                    payload: { failureMode: 'stall_timeout' }
                });
            }, COPY_STALL_TIMEOUT_MS);
            break;
        }

        case COPY_CREDIT_APP_EVENT_RECEIVED: {
            const message = action.payload?.message ?? {};
            const eventName: string = message.eventName;
            const eventTransactionId: string | undefined = message.eventTransactionId;
            const eventKeyData = message.eventKeyData ?? {};
            const eventCorrelationId: string | undefined = eventKeyData.correlationId;
            const currentDealRefId = getDealRefId(state);
            const currentCorrelationId = getContractingCorrelationId(state);
            const currentCopyStage = getCopyStage(state);
            const dealXgId = getDealXgId(state);
            const dealerId = dealerSelectors.getDealerId(state);

            const baseAttrs = {
                eventName,
                eventTransactionId,
                eventCorrelationId,
                currentCorrelationId,
                currentDealRefId,
                currentCopyStage,
                dealXgId,
                dealerId
            };

            const guardRejection: { event: string; reason: 'wrong_deal' | 'not_waiting' | null } | null =
                eventTransactionId !== currentDealRefId
                    ? { event: 'MV:CopyCreditApp - Unexpected Event', reason: 'wrong_deal' }
                    : eventCorrelationId !== currentCorrelationId
                    ? { event: 'MV:CopyCreditApp - Validation Mismatch', reason: null }
                    : currentCopyStage !== 'requesting'
                    ? { event: 'MV:CopyCreditApp - Unexpected Event', reason: 'not_waiting' }
                    : null;

            if (guardRejection) {
                logNewRelicPageAction(guardRejection.event, {
                    ...baseAttrs,
                    ...(guardRejection.reason && { reason: guardRejection.reason })
                });
                break;
            }

            logNewRelicPageAction('MV:CopyCreditApp - Received Event', {
                ...baseAttrs,
                stageDurationMs: computeStageDurationMs(state)
            });

            if (eventName === 'CreditApplications:Copied') {
                clearTimeout(copyStallTimer);
                if (!eventKeyData.creditAppId) {
                    const stageDurationMs = computeStageDurationMs(state);
                    store.dispatch(contractingCloseModal(false));
                    store.dispatch({ type: CONTRACTING_STAGE_COPY_FAILURE });
                    store.dispatch({
                        type: CONTRACTING_STAGE_COPY_REQUEST_FAILURE_EVENT,
                        payload: message
                    });
                    logNewRelicPageAction('MV:CopyCreditApp - copy-failure', {
                        correlationId: currentCorrelationId,
                        dealRefId: currentDealRefId,
                        dealXgId,
                        dealerId,
                        failureMode: 'copied_missing_credit_app_id',
                        stageDurationMs
                    });
                    break;
                }
                const copyInFlightSince = getCopyInFlightSince(state);
                logNewRelicPageAction('MV:CopyCreditApp - copy-success', {
                    correlationId: currentCorrelationId,
                    dealRefId: currentDealRefId,
                    dealXgId,
                    dealerId,
                    creditAppId: eventKeyData.creditAppId,
                    latencyMs: copyInFlightSince != null ? Date.now() - copyInFlightSince : null,
                    stageDurationMs: computeStageDurationMs(state)
                });
                pendingCopyCreditAppMessage = message;
                store.dispatch(
                    updateDealForCopyCreditApp({
                        correlationId: currentCorrelationId ?? '',
                        dealRefId: currentDealRefId ?? '',
                        dealXgDealId: dealXgId ?? '',
                        dealXgDealVersion: getDealXgVersion(state) || '',
                        newCreditAppId: eventKeyData.creditAppId,
                        dealerId: String(dealerId ?? '')
                    })
                );
                break;
            }

            if (eventName === 'CreditApplications:Error') {
                clearTimeout(copyStallTimer);
                const stageDurationMs = computeStageDurationMs(state);
                store.dispatch(contractingCloseModal(false));
                store.dispatch({ type: CONTRACTING_STAGE_COPY_FAILURE });
                store.dispatch({
                    type: CONTRACTING_STAGE_COPY_REQUEST_FAILURE_EVENT,
                    payload: message
                });
                logNewRelicPageAction('MV:CopyCreditApp - copy-failure', {
                    correlationId: currentCorrelationId,
                    dealRefId: currentDealRefId,
                    dealXgId,
                    dealerId,
                    failureMode: 'copy_error_event',
                    upstreamStatus: message.payload?.message?.status,
                    stageDurationMs
                });
                break;
            }

            logNewRelicPageAction('MV:CopyCreditApp - Unexpected Event', {
                ...baseAttrs,
                reason: 'unrecognized_event_name'
            });
            break;
        }

        case COPY_CREDIT_APP_DEAL_UPDATE_SUCCESS: {
            logNewRelicPageAction('MV:CopyCreditApp - deal-update-success', {
                correlationId: getContractingCorrelationId(state),
                dealRefId: getDealRefId(state),
                dealXgId: getDealXgId(state),
                dealerId: dealerSelectors.getDealerId(state),
                stageDurationMs: computeStageDurationMs(prevState)
            });
            bridgeDealUpdateToCopySuccess(store);
            break;
        }

        case COPY_CREDIT_APP_DEAL_UPDATE_FAILURE: {
            const errorBody = action.payload?.response?.error ?? action.payload?.error ?? {};
            logNewRelicPageAction('MV:CopyCreditApp - deal-update-failure', {
                correlationId: getContractingCorrelationId(state),
                dealRefId: getDealRefId(state),
                dealXgId: getDealXgId(state),
                dealerId: dealerSelectors.getDealerId(state),
                upstreamStatus: errorBody.upstreamStatus,
                phase: errorBody.phase,
                stageDurationMs: computeStageDurationMs(prevState)
            });
            bridgeDealUpdateToCopySuccess(store);
            break;
        }

        case COPY_CREDIT_APP_HTTP_FAILURE: {
            clearTimeout(copyStallTimer);
            const dealRefId = getDealRefId(state);
            const dealXgId = getDealXgId(state);
            const dealerId = dealerSelectors.getDealerId(state);
            const currentCorrelationId = getContractingCorrelationId(state);
            const errorBody = action.payload?.response?.error ?? action.payload?.error ?? {};
            const stageDurationMs = computeStageDurationMs(state);
            store.dispatch(contractingCloseModal(false));
            store.dispatch({ type: CONTRACTING_STAGE_COPY_FAILURE });
            store.dispatch({
                type: CONTRACTING_STAGE_COPY_REQUEST_FAILURE_EVENT,
                payload: { failureMode: 'http_failure', ...errorBody }
            });
            logNewRelicPageAction('MV:CopyCreditApp - copy-failure', {
                correlationId: currentCorrelationId,
                dealRefId,
                dealXgId,
                dealerId,
                failureMode: 'http_failure',
                upstreamStatus: errorBody.upstreamStatus,
                errorCode: errorBody.code,
                errorMessage: errorBody.message,
                stageDurationMs
            });
            break;
        }
    }
};
